import { useEffect, useRef, useState } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import {
  ClipboardList,
  ClipboardSignature,
  Factory,
  LogOut,
  Menu,
  Bot,
  PackageOpen,
  RefreshCw,
  Settings,
  Warehouse,
} from 'lucide-react';

import { BrandMark } from '../components/BrandMark';
import { useAuth } from '../context/AuthContext';
import { FACTORY_LOGIN_PATH, useFactoryWorkspace } from '../context/FactoryWorkspaceContext';
import { isApprovedFactoryManager, isApprovedFactoryWorker } from '../lib/factoryContext';
import { authService } from '../services/authService';
import { factoryService, factoryOrderCreatedLabel, factoryOrderScopeLabel, isFactoryWorkerRequiredError } from '../services/factoryService';
import type { FactoryWorkerQueueStatus } from '../services/factoryService';
import type { FactoryDeskOutletContext } from './deskOutlet';
import { FactoryOrderScopeProvider, useFactoryOrderScope } from './orderScope';

const factoryTabs = [
  { to: '/factory/orders', label: '订单', icon: ClipboardSignature, requiresOrder: false },
  { to: '/factory/shelf', label: '源货架', icon: Warehouse, requiresOrder: true },
  { to: '/factory/tasks', label: '任务', icon: ClipboardList, requiresOrder: true },
  { to: '/factory/goods', label: '成品智能体', icon: Bot, requiresOrder: true },
  { to: '/factory/release', label: '释放', icon: PackageOpen, requiresOrder: true },
];

const menuItemClass =
  'flex w-full items-center gap-2 rounded-md px-2.5 py-2 text-left text-sm text-[#E8ECF1] transition hover:bg-white/5';

type ShellGate = 'checking' | 'allowed' | 'capability_denied' | 'unreachable';

const JOB_TYPE_ZH: Record<string, string> = {
  layout: '页图/版面',
  ingest: '页图/版面',
  semantic_split: '语义切分',
  aggregate: '结构聚合',
  extract: '颗粒提取',
  figure_extract: '插图提取',
  enrich: 'RFE/PAP',
  ttq: '品题推荐',
  ipttq: '品题浓缩',
};

function sumByTypes(
  counts: Record<string, number> | undefined,
  types: string[] | undefined,
): number {
  if (!counts || !types?.length) return 0;
  let n = 0;
  for (const t of types) n += Math.max(0, Number(counts[t]) || 0);
  return n;
}

function formatWorkerQueueChip(q: FactoryWorkerQueueStatus | null): {
  text: string;
  title: string;
  tone: 'ok' | 'busy' | 'warn' | 'off';
} {
  if (!q) {
    return { text: '厂工 …', title: '正在读取厂工队列状态', tone: 'off' };
  }
  if (q.enabled === false) {
    return {
      text: '厂工关闭',
      title: 'FACTORY_WORKER_ENABLED=0：API-only，排队 Job 不会被领取。请在 ECS .env 设为 1 并重启。',
      tone: 'warn',
    };
  }
  if (!q.alive) {
    return {
      text: '厂工离线',
      title: '本进程 Worker 循环未运行；排队 Job 不会被领取',
      tone: 'warn',
    };
  }
  const prepCap = Math.max(1, Number(q.prepJobSlotCapacity ?? Math.ceil((q.jobSlotCapacity || 2) / 2)) || 1);
  const prepBusy = Math.min(prepCap, Math.max(0, Number(q.prepJobSlotsBusy) || 0));
  const pipeCap = Math.max(1, Number(q.pipelineJobSlotCapacity ?? Math.floor((q.jobSlotCapacity || 2) / 2)) || 1);
  const pipeBusy = Math.min(pipeCap, Math.max(0, Number(q.pipelineJobSlotsBusy) || 0));
  const queued = Math.max(0, Number(q.jobsQueued) || 0);
  const pageC = Math.max(1, Number(q.extractPageConcurrency) || 4);
  const text = `页图 ${prepBusy}/${prepCap} · 提取 ${pipeBusy}/${pipeCap} · 排${queued}`;
  const title = [
    `厂工 ${q.workerId}`,
    `页图准备槽 ${prepBusy}/${prepCap}（FACTORY_PREP_JOB_CONCURRENCY；layout 等）`,
    `提取/品题槽 ${pipeBusy}/${pipeCap}（FACTORY_PIPELINE_JOB_CONCURRENCY；extract/enrich/ttq…）`,
    `两槽独立：提取满载不会挡住页图准备`,
    `排队 ${queued} · DB 运行中 ${q.jobsRunningDb}`,
    `提取页并发上限 ${pageC}（FACTORY_EXTRACT_CONCURRENCY；单 Job 内页级）`,
    q.startedAt ? `启动于 ${q.startedAt}` : '',
  ].filter(Boolean).join('\n');
  const tone = prepBusy > 0 || pipeBusy > 0 || queued > 0 ? 'busy' : 'ok';
  return { text, title, tone };
}

function FactoryGridBackdrop() {
  return (
    <div
      className="pointer-events-none absolute inset-0 opacity-[0.07]"
      style={{
        backgroundImage:
          'linear-gradient(135deg, #fff 1px, transparent 1px), linear-gradient(45deg, #fff 1px, transparent 1px)',
        backgroundSize: '28px 28px',
      }}
      aria-hidden
    />
  );
}

function GateScreen({
  title,
  body,
  detail,
  detailTone = 'danger',
  onBack,
  onRetry,
  retryLabel = '重试连接',
  onEnterDiary,
}: {
  title: string;
  body: string;
  detail?: string | null;
  /** checking 用中性说明；失败态才用警示色 */
  detailTone?: 'info' | 'danger';
  onBack: () => void;
  onRetry?: () => void;
  retryLabel?: string;
  onEnterDiary?: () => void;
}) {
  return (
    <div className="relative flex min-h-dvh flex-col justify-center bg-[#15191f] px-4 py-12 text-[#E8ECF1]">
      <FactoryGridBackdrop />
      <div className="relative mx-auto w-full max-w-lg space-y-4">
        <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-[#2EC4B6]">
          Staff Channel · 产线
        </p>
        <h1 className="text-2xl font-semibold tracking-tight text-[#F2F5F8]">{title}</h1>
        <p className="text-sm leading-6 text-[#9AA3B0]">{body}</p>
        {detail ? (
          <p
            className={
              detailTone === 'info'
                ? 'text-sm leading-6 text-[#8B94A3]'
                : 'text-sm text-[#F07178]'
            }
          >
            {detail}
          </p>
        ) : null}
        <div className="flex flex-wrap gap-2">
          {onRetry ? (
            <button
              type="button"
              onClick={onRetry}
              className="rounded-md bg-[#2EC4B6] px-4 py-2.5 text-sm font-semibold text-[#15191f]"
            >
              {retryLabel}
            </button>
          ) : null}
          {onEnterDiary ? (
            <button
              type="button"
              onClick={onEnterDiary}
              className="rounded-md bg-[#2EC4B6] px-4 py-2.5 text-sm font-semibold text-[#15191f]"
            >
              登录颗粒日记
            </button>
          ) : null}
          <button
            type="button"
            onClick={onBack}
            className={
              onRetry || onEnterDiary
                ? 'rounded-md border border-[#3a4450] bg-[#15191f] px-4 py-2.5 text-sm font-semibold text-[#E8ECF1]'
                : 'rounded-md bg-[#2EC4B6] px-4 py-2.5 text-sm font-semibold text-[#15191f]'
            }
          >
            返回工厂登录
          </button>
        </div>
      </div>
    </div>
  );
}

function FactoryDesk({
  isResultsDesk,
  workerQueue,
  queueChip,
  isManager,
  refreshKey,
  refreshing,
  bumpDeskRefresh,
  backToFactoryLogin,
}: {
  isResultsDesk: boolean;
  workerQueue: FactoryWorkerQueueStatus | null;
  queueChip: ReturnType<typeof formatWorkerQueueChip>;
  isManager: boolean;
  refreshKey: number;
  refreshing: boolean;
  bumpDeskRefresh: () => void;
  backToFactoryLogin: () => void;
}) {
  const navigate = useNavigate();
  const { activeOrder, activeOrderId } = useFactoryOrderScope();
  const [shellMenuOpen, setShellMenuOpen] = useState(false);
  const [workerDetailOpen, setWorkerDetailOpen] = useState(false);
  const [orderHint, setOrderHint] = useState<string | null>(null);
  const orderHintTimer = useRef<number | null>(null);
  const shellMenuRef = useRef<HTMLDivElement | null>(null);

  const flashOrderHint = () => {
    setOrderHint('请先选择订单');
    if (orderHintTimer.current != null) window.clearTimeout(orderHintTimer.current);
    orderHintTimer.current = window.setTimeout(() => {
      setOrderHint(null);
      orderHintTimer.current = null;
    }, 2200);
  };

  const onScopedTabClick = (event: { preventDefault: () => void }, blocked: boolean) => {
    if (!blocked) return;
    event.preventDefault();
    flashOrderHint();
  };

  useEffect(() => {
    if (!shellMenuOpen) return;
    const onDoc = (e: MouseEvent) => {
      const el = shellMenuRef.current;
      if (el && !el.contains(e.target as Node)) {
        setShellMenuOpen(false);
        setWorkerDetailOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setShellMenuOpen(false);
        setWorkerDetailOpen(false);
      }
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [shellMenuOpen]);

  useEffect(() => () => {
    if (orderHintTimer.current != null) window.clearTimeout(orderHintTimer.current);
  }, []);

  return (
    <div className="factory-desk relative flex h-dvh flex-col overflow-hidden bg-[#15191f] text-[#E8ECF1]">
      <FactoryGridBackdrop />
      {workerQueue && (workerQueue.enabled === false || !workerQueue.alive) ? (
        <div className="relative z-50 border-b border-amber-500/40 bg-amber-500/15 px-4 py-2 text-center text-xs text-amber-100">
          {workerQueue.enabled === false
            ? '厂工已关闭（FACTORY_WORKER_ENABLED=0）：排队任务不会被领取，页图转图/提取不会开始。请在 ECS .env 改为 1 并 systemctl restart appfactory。'
            : '厂工离线：排队任务等待领取。请确认 AppFactory 进程已启动且 Worker 循环在跑。'}
        </div>
      ) : null}

      <header className="relative sticky top-0 z-40 border-b border-[#3a4450] bg-[#1e242c]/95 backdrop-blur">
        <div className="flex w-full items-center gap-3 px-3 py-3">
          <div className="min-w-0 flex-1">
            <div className="flex min-w-0 items-center gap-2.5">
              <BrandMark size={36} tint="#2EC4B6" alt="" className="shrink-0" />
              <span className="shrink-0 text-lg font-semibold tracking-wide text-[#2EC4B6] sm:text-xl">
                颗粒工厂
              </span>
              <span className="hidden h-4 w-px shrink-0 bg-[#3a4450] sm:block" aria-hidden />
              <button
                type="button"
                onClick={() => navigate('/factory/orders')}
                className="min-w-0 truncate text-left text-sm text-[#9AA3B0] hover:text-[#E8ECF1]"
                title={activeOrder ? '当前订单 · 点此去订单页切换' : '请先在订单页选择当前订单'}
              >
                {activeOrder ? factoryOrderScopeLabel(activeOrder) : '未选择订单'}
              </button>
              {activeOrder && factoryOrderCreatedLabel(activeOrder) ? (
                <span className="hidden shrink-0 text-xs text-[#8B94A3] sm:inline">
                  {factoryOrderCreatedLabel(activeOrder)}
                </span>
              ) : null}
            </div>
          </div>
          <nav className="hidden items-center gap-1 md:flex">
            {factoryTabs.map(({ to, label, icon: Icon, requiresOrder }) => {
              const blocked = requiresOrder && !activeOrderId;
              return (
                <NavLink
                  key={to}
                  to={to}
                  onClick={(event) => onScopedTabClick(event, blocked)}
                  title={blocked ? '请先选择订单' : undefined}
                  aria-disabled={blocked || undefined}
                  className={({ isActive }) =>
                    `inline-flex items-center gap-1.5 rounded-md px-3 py-2 text-sm transition ${
                      blocked
                        ? 'text-[#6B7380] hover:bg-white/5 hover:text-[#9AA3B0]'
                        : isActive
                          ? 'bg-[#2EC4B6]/15 font-medium text-[#2EC4B6]'
                          : 'text-[#9AA3B0] hover:bg-white/5 hover:text-[#E8ECF1]'
                    }`
                  }
                >
                  <Icon size={16} />
                  {label}
                </NavLink>
              );
            })}
          </nav>
          <div className="relative" ref={shellMenuRef}>
            <button
              type="button"
              onClick={() => {
                setShellMenuOpen((v) => !v);
                setWorkerDetailOpen(false);
              }}
              className={
                queueChip.tone === 'warn'
                  ? 'inline-flex h-9 w-9 items-center justify-center rounded-md border border-amber-500/40 bg-amber-500/10 text-amber-100 transition hover:border-amber-400/60'
                  : queueChip.tone === 'busy'
                    ? 'inline-flex h-9 w-9 items-center justify-center rounded-md border border-[#2EC4B6]/40 bg-[#2EC4B6]/10 text-[#2EC4B6] transition hover:border-[#2EC4B6]/70'
                    : 'inline-flex h-9 w-9 items-center justify-center rounded-md border border-[#3a4450] bg-[#15191f] text-[#9AA3B0] transition hover:border-[#2EC4B6]/50 hover:text-[#2EC4B6]'
              }
              aria-label="产线菜单"
              aria-expanded={shellMenuOpen}
              title="产线菜单"
            >
              <Menu size={16} />
            </button>
            {shellMenuOpen ? (
              <div
                role="menu"
                aria-label="产线菜单"
                className="absolute right-0 top-[calc(100%+8px)] z-50 w-[18.5rem] overflow-hidden rounded-xl border border-[#3a4450] bg-[#1e242c] shadow-xl"
              >
                <div className="border-b border-[#3a4450] px-3 py-2.5">
                  <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-[#2EC4B6]">
                    产线
                  </p>
                  <p className="mt-1 truncate text-sm text-[#9AA3B0]">
                    产线 Context{isManager ? ' · 管理员' : ''}
                  </p>
                </div>
                <div className="p-1.5">
                  <NavLink
                    to="/factory/settings"
                    role="menuitem"
                    onClick={() => setShellMenuOpen(false)}
                    className={({ isActive }) =>
                      `${menuItemClass} ${isActive ? 'bg-[#2EC4B6]/15 font-medium text-[#2EC4B6]' : ''}`
                    }
                  >
                    <Settings size={16} />
                    设置
                  </NavLink>
                  <button
                    type="button"
                    role="menuitem"
                    aria-expanded={workerDetailOpen}
                    title={queueChip.title}
                    onClick={() => setWorkerDetailOpen((v) => !v)}
                    className={menuItemClass}
                  >
                    <Factory size={16} className="shrink-0" />
                    <span className="min-w-0 flex-1 truncate">厂工闲忙</span>
                    <span className="max-w-[7.5rem] shrink-0 truncate text-[10px] text-[#8B94A3]">
                      {queueChip.text}
                    </span>
                  </button>
                  {workerDetailOpen ? (
                    <div className="mx-1 mb-1 rounded-md border border-[#3a4450] bg-[#15191f] px-2.5 py-2">
                      <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-[#2EC4B6]">
                        厂工 · 队列闲忙
                      </p>
                      {!workerQueue ? (
                        <p className="mt-2 text-xs text-[#9AA3B0]">
                          正在读取…（若一直如此请确认 AppFactory 已部署 /api/worker/queue）
                        </p>
                      ) : (
                        <dl className="mt-2 space-y-1.5 text-xs text-[#E8ECF1]">
                          <div className="flex justify-between gap-2">
                            <dt className="text-[#9AA3B0]">状态</dt>
                            <dd className="font-medium tabular-nums">
                              {workerQueue.enabled === false
                                ? '已关闭(ENABLED=0)'
                                : workerQueue.alive
                                  ? ((workerQueue.jobSlotsBusy || 0) > 0 ? '忙碌' : '空闲')
                                  : '离线'}
                            </dd>
                          </div>
                          <div className="flex justify-between gap-2">
                            <dt className="text-[#9AA3B0]">页图准备槽</dt>
                            <dd className="tabular-nums font-medium">
                              {workerQueue.prepJobSlotsBusy ?? '—'}/{workerQueue.prepJobSlotCapacity ?? '—'}
                            </dd>
                          </div>
                          <div className="flex justify-between gap-2">
                            <dt className="text-[#9AA3B0]">提取/品题槽</dt>
                            <dd className="tabular-nums font-medium">
                              {workerQueue.pipelineJobSlotsBusy ?? '—'}/{workerQueue.pipelineJobSlotCapacity ?? '—'}
                            </dd>
                          </div>
                          <div className="flex justify-between gap-2">
                            <dt className="text-[#9AA3B0]">排队 Job</dt>
                            <dd className="tabular-nums font-medium">{workerQueue.jobsQueued}</dd>
                          </div>
                          <div className="flex justify-between gap-2">
                            <dt className="text-[#9AA3B0]">页图排队</dt>
                            <dd className="tabular-nums font-medium">
                              {sumByTypes(workerQueue.byTypeQueued, workerQueue.prepJobTypes
                                || ['ingest', 'layout', 'semantic_split', 'aggregate'])}
                            </dd>
                          </div>
                          <div className="flex justify-between gap-2">
                            <dt className="text-[#9AA3B0]">提取排队</dt>
                            <dd className="tabular-nums font-medium">
                              {sumByTypes(workerQueue.byTypeQueued, workerQueue.pipelineJobTypes
                                || ['extract', 'enrich', 'ttq', 'ipttq'])}
                            </dd>
                          </div>
                          <div className="flex justify-between gap-2">
                            <dt className="text-[#9AA3B0]">DB 运行中</dt>
                            <dd className="tabular-nums font-medium">{workerQueue.jobsRunningDb}</dd>
                          </div>
                          <div className="flex justify-between gap-2">
                            <dt className="text-[#9AA3B0]">提取页并发</dt>
                            <dd className="tabular-nums font-medium">×{workerQueue.extractPageConcurrency}</dd>
                          </div>
                          <div className="flex justify-between gap-2">
                            <dt className="text-[#9AA3B0]">当前工序</dt>
                            <dd className="max-w-[9rem] truncate text-right font-medium">
                              {(workerQueue.currentJobs && workerQueue.currentJobs.length > 0
                                ? workerQueue.currentJobs
                                : workerQueue.currentJob
                                  ? [workerQueue.currentJob]
                                  : []
                              )
                                .map((j) => JOB_TYPE_ZH[j.jobType] || j.jobType)
                                .join('、') || '—'}
                            </dd>
                          </div>
                          <p className="border-t border-[#3a4450] pt-2 text-[10px] leading-relaxed text-[#8B94A3]">
                            Job 默认 2、页并发默认 4；启动有宽限秒数不领任务，避免 ECS 一启动就拉满。
                          </p>
                        </dl>
                      )}
                    </div>
                  ) : null}
                  <button
                    type="button"
                    role="menuitem"
                    disabled={refreshing}
                    onClick={() => {
                      bumpDeskRefresh();
                      setShellMenuOpen(false);
                    }}
                    className={`${menuItemClass} disabled:opacity-60`}
                  >
                    <RefreshCw size={16} className={refreshing ? 'animate-spin' : undefined} />
                    刷新产线数据
                  </button>
                  <button
                    type="button"
                    role="menuitem"
                    onClick={backToFactoryLogin}
                    className={menuItemClass}
                  >
                    <LogOut size={16} />
                    退出并返回工厂登录
                  </button>
                </div>
              </div>
            ) : null}
          </div>
        </div>
      </header>

      <main
        className={
          isResultsDesk
            ? 'relative mx-auto flex w-full min-h-0 max-w-none flex-1 flex-col overflow-hidden px-3 py-3 md:px-4'
            : 'relative mx-auto w-full max-w-5xl min-h-0 flex-1 overflow-y-auto px-4 py-5'
        }
      >
        <Outlet context={{ refreshKey, workerQueue } satisfies FactoryDeskOutletContext} />
      </main>
      {orderHint ? (
        <div
          role="status"
          className="pointer-events-none absolute inset-x-0 top-16 z-50 flex justify-center px-4"
        >
          <p className="rounded-md border border-[#3a4450] bg-[#1e242c] px-3 py-2 text-sm text-[#E8ECF1] shadow-xl">
            {orderHint}
          </p>
        </div>
      ) : null}

      <nav className="relative sticky bottom-0 z-40 border-t border-[#3a4450] bg-[#1e242c] md:hidden">
        <div className="grid grid-cols-5">
          {factoryTabs.map(({ to, label, icon: Icon, requiresOrder }) => {
            const blocked = requiresOrder && !activeOrderId;
            return (
              <NavLink
                key={to}
                to={to}
                onClick={(event) => onScopedTabClick(event, blocked)}
                title={blocked ? '请先选择订单' : undefined}
                aria-disabled={blocked || undefined}
                className={({ isActive }) =>
                  `flex flex-col items-center gap-1 py-2 text-[11px] ${
                    blocked ? 'text-[#5C6570]' : isActive ? 'text-[#2EC4B6]' : 'text-[#8B94A3]'
                  }`
                }
              >
                <Icon size={18} />
                {label}
              </NavLink>
            );
          })}
        </div>
      </nav>
    </div>
  );
}

export function FactoryShell() {
  const { pathname } = useLocation();
  const isResultsDesk = /\/factory\/tasks\/[^/]+\/results\/?$/.test(pathname);
  const { user } = useAuth();
  const { enterFactory, exitFactory, clearFactoryIntent } = useFactoryWorkspace();
  const navigate = useNavigate();
  const isManager = isApprovedFactoryManager(user);
  const [gate, setGate] = useState<ShellGate>('checking');
  const [gateDetail, setGateDetail] = useState<string | null>(null);
  const [gateAttempt, setGateAttempt] = useState(0);
  const [refreshKey, setRefreshKey] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const [workerQueue, setWorkerQueue] = useState<FactoryWorkerQueueStatus | null>(null);

  useEffect(() => {
    if (gate !== 'allowed') return;
    let cancelled = false;
    const load = async () => {
      try {
        const res = await factoryService.getWorkerQueue();
        if (!cancelled) setWorkerQueue(res.queue || null);
      } catch {
        if (!cancelled) setWorkerQueue(null);
      }
    };
    void load();
    const timer = window.setInterval(() => void load(), 8_000);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, [gate, refreshKey]);

  useEffect(() => {
    let cancelled = false;
    setGate('checking');
    setGateDetail(null);

    void (async () => {
      let approved = isApprovedFactoryWorker(user);
      if (!approved) {
        try {
          const fresh = await authService.fetchSession();
          approved = isApprovedFactoryWorker(fresh);
        } catch {
          approved = false;
        }
      }
      if (cancelled) return;
      if (!approved) {
        clearFactoryIntent();
        setGate('capability_denied');
        return;
      }

      try {
        // Distinguish dead process vs overloaded Node (health ok but shelf starved).
        try {
          const health = await factoryService.probeHealth(8_000);
          if (!health.ok) {
            throw new Error(`产线 /health HTTP ${health.status}：${health.bodyPreview}`);
          }
        } catch (healthErr) {
          if (cancelled) return;
          clearFactoryIntent();
          setGate('unreachable');
          setGateDetail(
            healthErr instanceof Error
              ? healthErr.message
              : String(healthErr),
          );
          return;
        }

        await factoryService.listShelf({ timeoutMs: 15_000 });
        if (cancelled) return;
        enterFactory();
        setGate('allowed');
      } catch (error) {
        if (cancelled) return;
        clearFactoryIntent();
        const message = error instanceof Error ? error.message : String(error);
        if (isFactoryWorkerRequiredError(error)) {
          setGate('capability_denied');
          setGateDetail(message);
          return;
        }
        const overloaded = /\/api\/shelf/.test(message) && /超时|timeout/i.test(message);
        setGate('unreachable');
        setGateDetail(
          overloaded
            ? `${message} · 产线进程可能过载（多任务页图准备占满 CPU）。可稍后重试，或降 ECS 上 FACTORY_JOB_CONCURRENCY。`
            : message,
        );
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [clearFactoryIntent, enterFactory, gateAttempt, user?.id]);

  const backToFactoryLogin = () => {
    void exitFactory(FACTORY_LOGIN_PATH);
  };

  const retryGate = () => {
    setGateDetail(null);
    setGateAttempt((n) => n + 1);
  };

  const bumpDeskRefresh = () => {
    if (refreshing) return;
    setRefreshing(true);
    setRefreshKey((key) => key + 1);
    window.setTimeout(() => setRefreshing(false), 700);
  };

  const queueChip = formatWorkerQueueChip(workerQueue);

  if (gate === 'checking') {
    return (
      <GateScreen
        title="颗粒工厂"
        body="正在校验工厂工作人员能力…"
        detail="产线刚启动时鉴权可能稍慢，一般十几秒内完成；若超时会提示失败，届时可重试。"
        detailTone="info"
        onBack={backToFactoryLogin}
        onRetry={retryGate}
        retryLabel="重新校验"
      />
    );
  }

  if (gate === 'capability_denied') {
    return (
      <GateScreen
        title="颗粒工厂"
        body="当前日记账户尚未获得已批准的 FactoryWorker 能力，无法进入工厂产线壳。是否改为登录颗粒日记？"
        detail={gateDetail}
        onBack={backToFactoryLogin}
        onEnterDiary={() => {
          clearFactoryIntent();
          navigate('/', { replace: true });
        }}
      />
    );
  }

  if (gate === 'unreachable') {
    return (
      <GateScreen
        title="颗粒工厂"
        body="暂时无法连接工厂产线服务。请确认 factory.kelilearning.com 可用，且 ECS 验权指向与登录一致的 AppFunctor。"
        detail={gateDetail}
        onBack={backToFactoryLogin}
        onRetry={retryGate}
        retryLabel="重试连接"
      />
    );
  }


  return (
    <FactoryOrderScopeProvider refreshKey={refreshKey}>
      <FactoryDesk
        isResultsDesk={isResultsDesk}
        workerQueue={workerQueue}
        queueChip={queueChip}
        isManager={isManager}
        refreshKey={refreshKey}
        refreshing={refreshing}
        bumpDeskRefresh={bumpDeskRefresh}
        backToFactoryLogin={backToFactoryLogin}
      />
    </FactoryOrderScopeProvider>
  );
}
